import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

interface DateRange {
  from?: Date;
  to?: Date;
}

interface SimpleDateRangePickerProps {
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  className?: string;
  buttonClassName?: string;
}

export function SimpleDateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
  buttonClassName,
}: SimpleDateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [startDate, setStartDate] = React.useState<Date | undefined>(dateRange?.from);
  const [endDate, setEndDate] = React.useState<Date | undefined>(dateRange?.to);

  // Update local state when props change
  React.useEffect(() => {
    setStartDate(dateRange?.from);
    setEndDate(dateRange?.to);
  }, [dateRange]);

  // Format the date display text
  const dateDisplayText = React.useMemo(() => {
    if (!startDate) {
      return "Pick a date range";
    }
    
    if (endDate) {
      return `${format(startDate, "LLL dd, y")} - ${format(endDate, "LLL dd, y")}`;
    }
    
    return format(startDate, "LLL dd, y");
  }, [startDate, endDate]);

  // Handle date selection
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value) : undefined;
    setStartDate(date);
    
    if (date) {
      onDateRangeChange({ from: date, to: endDate });
    } else {
      onDateRangeChange(endDate ? { from: undefined, to: endDate } : undefined);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value ? new Date(e.target.value) : undefined;
    setEndDate(date);
    
    if (startDate) {
      onDateRangeChange({ from: startDate, to: date });
      if (date) setOpen(false);
    } else if (date) {
      onDateRangeChange({ from: date, to: undefined });
    } else {
      onDateRangeChange(undefined);
    }
  };

  const formatDateForInput = (date?: Date) => {
    return date ? format(date, "yyyy-MM-dd") : "";
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            className={cn(
              "h-8 w-full justify-start text-left text-xs font-normal",
              buttonClassName,
              !startDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
            <span>{dateDisplayText}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto space-y-3 bg-white p-3">
          <div className="space-y-1.5">
            <label htmlFor="start-date" className="text-xs font-medium">
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              className="w-full rounded-md border bg-white p-2 text-xs"
              value={formatDateForInput(startDate)}
              onChange={handleStartDateChange}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="end-date" className="text-xs font-medium">
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              className="w-full rounded-md border bg-white p-2 text-xs"
              value={formatDateForInput(endDate)}
              onChange={handleEndDateChange}
              min={startDate ? formatDateForInput(startDate) : ""}
            />
          </div>
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                onDateRangeChange(undefined);
                setStartDate(undefined);
                setEndDate(undefined);
                setOpen(false);
              }}
              className="mr-2 h-8 text-xs"
            >
              Clear
            </Button>
            <Button 
              size="sm" 
              onClick={() => setOpen(false)}
              className="h-8 text-xs"
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default SimpleDateRangePicker;
